import { createLogger } from "../utils/logger.js";
//#region ../src/sensors/permission-checker.ts
/**
* Permission Checker Module
*
* Provides utilities to check and verify mandatory device permissions
* required for AR+GPS tracking: WebXR (immersive-ar), Camera, and Geolocation.
*
* This module is used by the setup modal to verify permissions BEFORE
* the user enters AR mode, providing clear feedback on what's missing.
*/
const log = createLogger("PermissionChecker");
let fileSystemState = {
	folderSelected: false,
	writeVerified: false
};
/**
* Reset file system permission state.
* Exported for testing purposes.
* @internal
*/
function resetFileSystemState() {
	fileSystemState = {
		folderSelected: false,
		writeVerified: false
	};
}
/**
* Set file system permission state.
* Used by the storage module after folder selection and write verification.
* @internal
*/
function setFileSystemState(state) {
	fileSystemState = {
		...fileSystemState,
		...state
	};
}
/**
* Check file system access permission status.
*
* User Feedback Issue #1: File system access is critical for recording.
* This function checks:
* 1. If OPFS (Origin Private File System) is available
* 2. If storage has been initialized
* 3. If write access has been verified
*
* Note: We now use OPFS instead of showDirectoryPicker because it works
* consistently on Android Chrome, iOS Safari, and Desktop browsers.
*
* @returns Permission status for file system access
*/
function checkFileSystemPermission() {
	if (typeof navigator === "undefined" || !navigator.storage || typeof navigator.storage.getDirectory !== "function") return {
		supported: false,
		granted: null,
		error: "Origin Private File System (OPFS) not supported. Use Chrome 86+, Safari 15.2+, or Firefox 111+."
	};
	if (!fileSystemState.folderSelected) return {
		supported: true,
		granted: null
	};
	if (!fileSystemState.writeVerified) return {
		supported: true,
		granted: false,
		error: fileSystemState.writeError || "Storage write failed. Please try again."
	};
	return {
		supported: true,
		granted: true
	};
}
/**
* Check if WebXR immersive-ar mode is supported.
* Does not request a session, only checks support.
*/
async function checkWebXRSupport() {
	if (!navigator.xr) return {
		supported: false,
		granted: null,
		error: "WebXR API not available. Use a compatible browser like Chrome on Android."
	};
	try {
		if (!await navigator.xr.isSessionSupported("immersive-ar")) return {
			supported: false,
			granted: null,
			error: "AR mode not supported. Ensure you have ARCore/ARKit installed."
		};
		return {
			supported: true,
			granted: true
		};
	} catch (err) {
		log.error("WebXR support check failed:", err);
		return {
			supported: false,
			granted: null,
			error: "Failed to check AR support. Please refresh and try again."
		};
	}
}
/**
* Check geolocation permission status without triggering a prompt.
* Uses the Permissions API if available, otherwise returns unknown state.
*/
async function checkGeolocationPermission() {
	if (!navigator.geolocation) return {
		supported: false,
		granted: null,
		error: "Geolocation API not available in this browser."
	};
	if (navigator.permissions) try {
		const result = await navigator.permissions.query({ name: "geolocation" });
		if (result.state === "granted") return {
			supported: true,
			granted: true
		};
		else if (result.state === "denied") return {
			supported: true,
			granted: false,
			error: "Location access denied. Please enable in browser settings."
		};
		return {
			supported: true,
			granted: null
		};
	} catch {}
	return {
		supported: true,
		granted: null
	};
}
/**
* Request geolocation permission by triggering a position request.
* This will show the browser's permission prompt if not already granted.
*
* @param timeoutMs - Maximum time to wait for position (default 10s)
* @returns Updated permission status after request
*/
async function requestGeolocationPermission(timeoutMs = 1e4) {
	if (!navigator.geolocation) return {
		supported: false,
		granted: null,
		error: "Geolocation API not available in this browser."
	};
	return new Promise((resolve) => {
		navigator.geolocation.getCurrentPosition(() => {
			log.info("Geolocation permission granted");
			resolve({
				supported: true,
				granted: true
			});
		}, (error) => {
			log.warn("Geolocation error:", error.message);
			if (error.code === error.PERMISSION_DENIED) resolve({
				supported: true,
				granted: false,
				error: "Location access denied. Please enable in browser settings."
			});
			else if (error.code === error.POSITION_UNAVAILABLE) resolve({
				supported: true,
				granted: null,
				error: "Location unavailable. Check GPS is enabled on your device."
			});
			else resolve({
				supported: true,
				granted: null,
				error: "Location request timed out. Please try again."
			});
		}, {
			enableHighAccuracy: true,
			timeout: timeoutMs,
			maximumAge: 0
		});
	});
}
/**
* Check camera permission status without triggering a prompt.
* Uses the Permissions API if available.
*/
async function checkCameraPermission() {
	if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return {
		supported: false,
		granted: null,
		error: "Camera API not available. Use HTTPS and a modern browser."
	};
	if (navigator.permissions) try {
		const result = await navigator.permissions.query({ name: "camera" });
		if (result.state === "granted") return {
			supported: true,
			granted: true
		};
		else if (result.state === "denied") return {
			supported: true,
			granted: false,
			error: "Camera access denied. Please enable in browser settings."
		};
		return {
			supported: true,
			granted: null
		};
	} catch {}
	return {
		supported: true,
		granted: null
	};
}
/**
* Request camera permission by triggering getUserMedia.
* This will show the browser's permission prompt if not already granted.
*
* @returns Updated permission status after request
*/
async function requestCameraPermission() {
	if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return {
		supported: false,
		granted: null,
		error: "Camera API not available. Use HTTPS and a modern browser."
	};
	try {
		const stream = await navigator.mediaDevices.getUserMedia({ video: true });
		for (const track of stream.getTracks()) track.stop();
		log.info("Camera permission granted");
		return {
			supported: true,
			granted: true
		};
	} catch (err) {
		log.warn("Camera permission error:", err);
		const error = err;
		if (error.name === "NotAllowedError") return {
			supported: true,
			granted: false,
			error: "Camera access denied. Please enable in browser settings."
		};
		else if (error.name === "NotFoundError") return {
			supported: false,
			granted: null,
			error: "No camera found on this device."
		};
		return {
			supported: true,
			granted: null,
			error: "Camera access failed. Please try again."
		};
	}
}
/**
* Check device orientation permission status.
* On iOS 13+, this requires explicit permission request.
*/
function checkOrientationPermission() {
	if (typeof DeviceOrientationEvent === "undefined") return Promise.resolve({
		supported: false,
		granted: null,
		error: "Device orientation not supported."
	});
	if (typeof DeviceOrientationEvent.requestPermission === "function") return Promise.resolve({
		supported: true,
		granted: null
	});
	return Promise.resolve({
		supported: true,
		granted: true
	});
}
/**
* Request device orientation permission (iOS 13+).
* On other platforms, this is a no-op that returns granted.
*/
async function requestOrientationPermission() {
	if (typeof DeviceOrientationEvent === "undefined") return {
		supported: false,
		granted: null,
		error: "Device orientation not supported."
	};
	const DeviceOrientationEventWithPermission = DeviceOrientationEvent;
	if (typeof DeviceOrientationEventWithPermission.requestPermission === "function") try {
		if (await DeviceOrientationEventWithPermission.requestPermission() === "granted") {
			log.info("Orientation permission granted");
			return {
				supported: true,
				granted: true
			};
		} else return {
			supported: true,
			granted: false,
			error: "Motion & orientation access denied."
		};
	} catch {
		return {
			supported: true,
			granted: null,
			error: "Orientation permission request failed."
		};
	}
	return {
		supported: true,
		granted: true
	};
}
/**
* Check all permissions without triggering any prompts.
* Returns current known state of each permission.
*/
async function checkAllPermissions() {
	const [webxr, geolocation, camera, orientation] = await Promise.all([
		checkWebXRSupport(),
		checkGeolocationPermission(),
		checkCameraPermission(),
		checkOrientationPermission()
	]);
	const fileSystem = checkFileSystemPermission();
	return {
		webxr,
		geolocation,
		camera,
		orientation,
		fileSystem,
		allMandatoryReady: webxr.supported && webxr.granted === true && geolocation.supported && geolocation.granted === true && camera.supported && camera.granted === true && fileSystem.supported && fileSystem.granted === true
	};
}
/**
* Request all mandatory permissions that haven't been granted yet.
* Shows browser prompts for each permission that needs requesting.
*
* This now includes the WebXR+Depth probe to trigger the "3D map" permission
* upfront, rather than surprising users with a second prompt after "Enter AR".
*
* @returns Updated permission check result after requests
*/
async function requestAllPermissions() {
	const result = await checkAllPermissions();
	if (result.webxr.supported) result.webxr = await requestWebXRWithDepthPermission();
	if (result.geolocation.supported && result.geolocation.granted !== true) result.geolocation = await requestGeolocationPermission();
	if (result.camera.supported && result.camera.granted !== true) result.camera = await requestCameraPermission();
	if (result.orientation.supported && result.orientation.granted !== true) result.orientation = await requestOrientationPermission();
	result.fileSystem = checkFileSystemPermission();
	result.allMandatoryReady = result.webxr.supported && result.webxr.granted === true && result.geolocation.supported && result.geolocation.granted === true && result.camera.supported && result.camera.granted === true && result.fileSystem.supported && result.fileSystem.granted === true;
	return result;
}
/**
* Request WebXR permission with depth-sensing by starting a probe session.
*
* The depth-sensing permission (ARCore's "3D map of surroundings") is only
* triggered when an XR session is actually started with depth-sensing options.
* This function starts a minimal probe session to trigger the permission prompt,
* then immediately ends it.
*
* This allows the app to request all AR permissions upfront during the
* setup flow, rather than surprising users with a second prompt when
* they click "Enter AR".
*
* @returns Permission status indicating if AR+depth access was granted
*/
async function requestWebXRWithDepthPermission() {
	if (!navigator.xr) return {
		supported: false,
		granted: null,
		error: "WebXR not available"
	};
	try {
		const session = await navigator.xr.requestSession("immersive-ar", {
			requiredFeatures: ["local-floor"],
			optionalFeatures: ["depth-sensing"],
			depthSensing: {
				usagePreference: ["cpu-optimized"],
				dataFormatPreference: ["luminance-alpha", "float32"]
			}
		});
		try {
			await session.end();
		} catch (endError) {
			log.warn("Probe session end failed (permission still granted):", endError);
		}
		log.info("WebXR with depth-sensing permission granted");
		return {
			supported: true,
			granted: true
		};
	} catch (err) {
		const error = err;
		log.warn("WebXR depth probe failed:", error);
		if (error.name === "NotAllowedError") return {
			supported: true,
			granted: false,
			error: "AR access denied"
		};
		return {
			supported: false,
			granted: null,
			error: error.message
		};
	}
}
//#endregion
export { checkAllPermissions, checkCameraPermission, checkFileSystemPermission, checkGeolocationPermission, checkOrientationPermission, checkWebXRSupport, requestAllPermissions, requestCameraPermission, requestGeolocationPermission, requestOrientationPermission, requestWebXRWithDepthPermission, resetFileSystemState, setFileSystemState };
